import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

/**
 * DocumentGenerationPanel - A reusable component for generating and viewing documents
 *
 * @param {string} entityType - The type of entity (contact, sale, plot, work_order)
 * @param {string|number} entityId - The ID of the entity
 * @param {string} className - Optional additional CSS classes
 * @param {boolean} compact - If true, renders in a more compact format
 */
const DocumentGenerationPanel = ({ entityType, entityId, className = '', compact = false }) => {
  const { token } = useContext(AppContext);
  const [templates, setTemplates] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadingDocuments, setLoadingDocuments] = useState(true);
  const [warnings, setWarnings] = useState([]);
  const [pdfAvailable, setPdfAvailable] = useState(true);
  const [pdfError, setPdfError] = useState(null);

  // Check if PDF generation is available
  useEffect(() => {
    const checkPdfAvailability = async () => {
      try {
        const response = await fetch('/api/documents/status', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setPdfAvailable(data.pdfGenerationAvailable);
          if (!data.pdfGenerationAvailable) {
            setPdfError(data.hint || data.error || 'PDF generation is not available');
          }
        }
      } catch (err) {
        console.error('Error checking PDF availability:', err);
      }
    };
    checkPdfAvailability();
  }, [token]);

  // Fetch available templates for this entity type
  useEffect(() => {
    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const response = await fetch(`/api/document-templates?entityType=${entityType}&isActive=true`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setTemplates(data);
        }
      } catch (err) {
        console.error('Error fetching templates:', err);
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, [entityType, token]);

  // Fetch previously generated documents for this entity
  useEffect(() => {
    const fetchDocuments = async () => {
      setLoadingDocuments(true);
      try {
        const response = await fetch(`/api/documents/entity/${entityType}/${entityId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setDocuments(data);
        }
      } catch (err) {
        console.error('Error fetching documents:', err);
      } finally {
        setLoadingDocuments(false);
      }
    };
    if (entityId) {
      fetchDocuments();
    }
  }, [entityType, entityId, token]);

  const handleGenerate = async () => {
    if (!selectedTemplate) {
      setError('Please select a template');
      return;
    }

    setIsGenerating(true);
    setError('');
    setSuccess('');
    setWarnings([]);

    try {
      const response = await fetch('/api/documents/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          templateId: parseInt(selectedTemplate),
          entityType,
          entityId: String(entityId),
          save: true
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Document generated successfully');
        setSelectedTemplate('');

        // Show any template warnings
        if (data.templateWarnings && data.templateWarnings.length > 0) {
          setWarnings(data.templateWarnings);
        }

        // Refresh documents list
        const docsResponse = await fetch(`/api/documents/entity/${entityType}/${entityId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (docsResponse.ok) {
          setDocuments(await docsResponse.json());
        }
        // Auto-download the generated document
        if (data.document?.id) {
          handleDownload(data.document.id, data.document.fileName);
        }
      } else {
        // Parse error message for common issues
        let errorMsg = data.error || 'Failed to generate document';

        // Provide helpful context for common errors
        if (errorMsg.includes('associated') || errorMsg.includes('alias')) {
          errorMsg = 'Server configuration error. Please contact your administrator.';
        } else if (errorMsg.includes('not found')) {
          errorMsg = `The ${entityType} could not be found. It may have been deleted.`;
        } else if (errorMsg.includes('Template')) {
          errorMsg = `Template error: ${errorMsg}`;
        }

        setError(errorMsg);
      }
    } catch (err) {
      console.error('Error generating document:', err);
      setError('Failed to connect to server. Please check your connection and try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async (docId, fileName) => {
    try {
      const response = await fetch(`/api/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || 'document.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Error downloading document:', err);
    }
  };

  const handleView = async (docId) => {
    try {
      const response = await fetch(`/api/documents/${docId}/view`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      }
    } catch (err) {
      console.error('Error viewing document:', err);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await fetch(`/api/documents/${docId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setDocuments(prev => prev.filter(d => d.id !== docId));
      }
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  // If no templates available, don't render anything
  if (loadingTemplates) {
    return (
      <div className={`${className}`}>
        <div className="animate-pulse flex items-center gap-2 text-sm text-gray-400">
          <DocumentTextIcon className="h-4 w-4" />
          Loading document templates...
        </div>
      </div>
    );
  }

  if (templates.length === 0) {
    return null; // Don't show panel if no templates configured for this entity type
  }

  const entityLabel = {
    contact: 'Contact',
    sale: 'Sale',
    plot: 'Plot',
    work_order: 'Work Order'
  }[entityType] || entityType;

  if (compact) {
    return (
      <div className={`${className}`}>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between text-sm font-medium text-gray-700 hover:text-gray-900"
        >
          <span className="flex items-center gap-2">
            <DocumentTextIcon className="h-4 w-4" />
            Documents ({documents.length})
          </span>
          {isExpanded ? (
            <ChevronUpIcon className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4" />
          )}
        </button>

        {isExpanded && (
          <div className="mt-3 space-y-3">
            {/* PDF Availability Warning */}
            {!pdfAvailable && (
              <div className="text-xs text-red-600 bg-red-50 rounded p-2">
                <div className="flex items-start gap-1">
                  <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" />
                  <span>PDF generation is not available. {pdfError}</span>
                </div>
              </div>
            )}

            {/* Generate Section */}
            <div className="flex gap-2">
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                disabled={!pdfAvailable}
                className="flex-1 text-sm border rounded-md px-2 py-1.5 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Select template...</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={isGenerating || !selectedTemplate || !pdfAvailable}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isGenerating ? 'Generating...' : 'Generate'}
              </button>
            </div>

            {error && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </p>
            )}
            {success && (
              <p className="text-sm text-green-600">{success}</p>
            )}
            {warnings.length > 0 && (
              <div className="text-sm text-amber-600 bg-amber-50 rounded p-2">
                <div className="flex items-start gap-1">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Template warnings:</p>
                    <ul className="list-disc list-inside text-xs mt-1">
                      {warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Documents List */}
            {documents.length > 0 && (
              <div className="space-y-1">
                {documents.slice(0, 5).map(doc => (
                  <div key={doc.id} className="flex items-center justify-between text-sm py-1">
                    <span className="truncate flex-1 text-gray-700" title={doc.fileName}>
                      {doc.template?.name || doc.fileName}
                    </span>
                    <div className="flex items-center gap-1 ml-2">
                      <button
                        type="button"
                        onClick={() => handleView(doc.id)}
                        className="p-1 text-gray-500 hover:text-indigo-600"
                        title="View"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownload(doc.id, doc.fileName)}
                        className="p-1 text-gray-500 hover:text-indigo-600"
                        title="Download"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(doc.id)}
                        className="p-1 text-gray-500 hover:text-red-600"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {documents.length > 5 && (
                  <p className="text-xs text-gray-500">+{documents.length - 5} more documents</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Full panel version
  return (
    <div className={`border-t pt-4 ${className}`}>
      <dt className="text-sm font-medium text-gray-500 mb-3 flex items-center">
        <DocumentTextIcon className="h-4 w-4 mr-1" />
        Documents
      </dt>
      <dd>
        {/* PDF Availability Warning */}
        {!pdfAvailable && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
            <div className="flex items-start gap-2">
              <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 text-red-500" />
              <div>
                <p className="font-medium">PDF generation is not available</p>
                <p className="text-xs mt-1">{pdfError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Generate Document */}
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Generate New Document
          </label>
          <div className="flex gap-2">
            <select
              value={selectedTemplate}
              onChange={(e) => { setSelectedTemplate(e.target.value); setError(''); setSuccess(''); }}
              disabled={!pdfAvailable}
              className="flex-1 text-sm border rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Select a template...</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} {t.category && `(${t.category})`}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={isGenerating || !selectedTemplate || !pdfAvailable}
              className="inline-flex items-center px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Generating...
                </>
              ) : (
                <>
                  <DocumentTextIcon className="h-4 w-4 mr-1.5" />
                  Generate
                </>
              )}
            </button>
          </div>
          {error && (
            <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
              <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </p>
          )}
          {success && (
            <p className="mt-2 text-sm text-green-600">{success}</p>
          )}
          {warnings.length > 0 && (
            <div className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-500" />
                <div>
                  <p className="font-medium">Template generated with warnings:</p>
                  <ul className="list-disc list-inside text-xs mt-1 space-y-1">
                    {warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                  <p className="text-xs mt-2 text-amber-600">
                    These warnings may indicate issues with your template. Edit the template to fix variable references.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Previously Generated Documents */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">
            Generated Documents ({documents.length})
          </label>
          {loadingDocuments ? (
            <div className="text-sm text-gray-400">Loading...</div>
          ) : documents.length > 0 ? (
            <div className="border rounded-md divide-y divide-gray-200 max-h-48 overflow-y-auto">
              {documents.map(doc => (
                <div key={doc.id} className="flex items-center justify-between px-4 py-2 hover:bg-gray-50">
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {doc.template?.name || doc.fileName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(doc.createdAt).toLocaleDateString()} at {new Date(doc.createdAt).toLocaleTimeString()}
                      {doc.generator && ` by ${doc.generator.firstName} ${doc.generator.lastName}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleView(doc.id)}
                      className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                      title="View"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDownload(doc.id, doc.fileName)}
                      className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                      title="Download"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(doc.id)}
                      className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">
              No documents generated yet for this {entityLabel.toLowerCase()}.
            </p>
          )}
        </div>
      </dd>
    </div>
  );
};

export default DocumentGenerationPanel;
